import { DataType, EventsPerDayType } from '@/components/MyPlanStep/WeeklyCalendar';
import { COMPLETE, INCOMPLETE, IN_PROGRESS } from '@/constants/action';
import { CronofyProfile } from '@/gql/cronofy/types';
import { createExternalActionAdapter } from '@/pages/WeeklyPlan/MyWeeklyPlan/helpers';
import { useCalendarMonthlyStore } from '@/stores/useCalendar';
import { Action, ProgressStatus, isPlannedAction } from '@/types/actions';
import { Block } from '@/types/block';
import { Category } from '@/types/category';
import { ActionEventType, ActionType, CategoryType, ExternalEventType, PlannedActionType } from '@/types/myPlan';
import { calculateTotalDuration, getActionsByBlockId, getActionsByCategoryId } from '@/utils/action';
import { getTotalDurationOfStarredActions } from '@/utils/events';
import { fixUncategorizedName, humanDuration } from '@/utils/index';
import { UniqueIdentifier } from '@dnd-kit/core';
import {
  add,
  addDays,
  addMonths,
  addWeeks,
  addYears,
  format,
  isBefore,
  isEqual,
  parseISO,
} from 'date-fns';
import { isEmpty, orderBy } from 'lodash';

import {
  endOfTheWeek,
  formatDateToString,
  getDaysWithinDatesRange,
  fastForwardRecurrenceCursor,
  getExceptionDates,
  getIncrementInterval,
  getLocalDateStringInTimezone,
  isExceptionDate,
  isValidWeeklyDay,
  localDateToUTC,
  shouldIncrementByWeek,
  startOfTheWeek,
  utcToLocalDate,
} from './calendar';

export type ItemType = Record<UniqueIdentifier, ActionEventType[]>;

function createActionEvent(action: ActionType): ActionEventType {
  return {
    id: action.id,
    // TODO: we should not use "as" here, but fixing it requires basically a
    // full rewrite of the calendars events handling
    action: action as PlannedActionType,
    actionId: action.id,
    typeName: action.typeName,
  };
}

function formatActions(actions: ActionType[]): ActionEventType[] {
  return actions?.map(createActionEvent);
}

function makeBlockItemKey(blockId: string) {
  return `block-${blockId}`;
}

function eventFor(event: ActionEventType, day: string) {
  if (!event.action.scheduledDate) {
    return;
  }

  // Compare the action's calendar date in its timezone with the column's date.
  // This keeps late-evening times (e.g. Sunday 6 PM MST) on the correct week day
  // instead of the UTC date (e.g. Monday).
  const eventLocalDateStr = getLocalDateStringInTimezone(
    event.action.scheduledDate,
    event.action.scheduledTime,
    event.action.timezone,
    event.action.gmtOffset,
  );
  const dayDateStr = format(parseISO(day), 'yyyy-MM-dd');

  if (eventLocalDateStr === dayDateStr) {
    return event;
  }

  return undefined;
}

function groupActionsByBlockId(blocks: Block[], actions: Action[]) {
  return blocks.reduce<ItemType>((acc, curr) => {
    const blockActions: ActionEventType[] = formatActions(getActionsByBlockId(curr.id, actions) as ActionType[]);

    const key = makeBlockItemKey(curr.id);

    return {
      ...acc,
      [key]: blockActions,
    };
  }, {});
}

function groupEventsByWeekDay(category: CategoryType[], weekDays: string[]) {
  const events: Record<string, ActionEventType[]> = category.reduce((acc, c) => {
    let keys: Record<string, ActionEventType[]> = {};

    weekDays.forEach((day, index) => {
      keys[`event-${c.id}-${index + 1}`] = [];
    });

    return {
      ...acc,
      ...keys,
    };
  }, {});

  category?.forEach((c) => {
    weekDays?.forEach((day, index) => {
      const key = `event-${c.id}-${index + 1}`;

      let filteredEvents: ActionEventType[] = [];

      c?.actions?.forEach(({ event }) => {
        const eventOnDay = eventFor(event, day);

        if (eventOnDay) {
          filteredEvents.push(eventOnDay);
        }
      });

      events[key] = filteredEvents;
    });
  });

  return events;
}

function getEventByActionId(actions: Action[], actionId: string) {
  const action = actions.find((action) => action.id === actionId);

  if (!action?.event?.id) {
    return undefined;
  }

  return {
    id: action.event.id,
    action,
    actionId: action.id,
    typeName: 'Event',
  };
}

function getEventById(actions: Action[], eventId: string) {
  const action = actions.find((action) => action?.event?.id === eventId);

  if (!action?.event?.id) {
    return undefined;
  }

  return {
    id: action.event.id,
    action,
    actionId: action.id,
    typeName: 'Event',
  };
}

function checkIfEventExists(id: string, events: ActionEventType[]): boolean {
  return events.findIndex((event) => event.id === id) !== -1;
}

function isSameEventDay(activeDay: string, overDay: string) {
  return activeDay === overDay;
}

function getDailyActionBackgroundColor(action: Action, selectedDate: Date) {
  if (!action.scheduledDate) {
    return 'gray.900';
  }

  const eventDate = utcToLocalDate(action.scheduledDate, action.scheduledTime, action.timezone, action.gmtOffset);

  if (formatDateToString(eventDate) === formatDateToString(selectedDate)) {
    return 'gray.900';
  }

  return 'gray.950';
}

function getWeeklyActionBackgroundColor(action: Action, weeklyPlanId: string) {
  if (action.weeklyPlanId === weeklyPlanId) {
    return 'gray.900';
  }

  return 'gray.950';
}

function makeEvents(
  category: Category[],
  action: Action[],
  externalEvents: ExternalEventType[],
  cronofyProfiles?: CronofyProfile[],
) {
  let events: ActionEventType[] = [];
  const selectedDate = useCalendarMonthlyStore.getState().selectedDate;
  const startDate = selectedDate && startOfTheWeek(selectedDate);
  const endDate = selectedDate && endOfTheWeek(selectedDate);

  const weekDays = getDaysWithinDatesRange(startDate, endDate);

  category.forEach((category) => {
    const actions = orderBy(getActionsByCategoryId(action, category.id)?.filter(isPlannedAction), [
      'scheduledDate',
      'scheduledTime',
      'title',
    ]);

    const actionEvents: ActionEventType[] = actions?.flatMap((action) => {
      const recurrence: any = action.event?.recurrence;

      // Treat anything without a usable, known frequency (missing/empty rules, '' or an
      // unrecognized frequency) as non-recurring — otherwise the loop below never advances and
      // hangs the browser. getIncrementInterval returns null for all of those cases.
      if (!recurrence || !getIncrementInterval(recurrence.rules?.[0])) {
        return [
          {
            id: String(action.event?.id),
            action: { ...action, typeName: 'Action' },
            actionId: action.id,
            typeName: 'Event',
          },
        ];
      }

      const fullEndDate = recurrence?.rules?.[0]?.until;

      const fullDate = action?.event?.start?.split('T')[0];

      const { frequency } = recurrence.rules[0];
      const start = utcToLocalDate(
        fullDate || action.scheduledDate,
        action.scheduledTime,
        action.timezone,
        action.gmtOffset,
      );
      const until = fullEndDate
        ? utcToLocalDate(fullEndDate, action.scheduledTime, action.timezone, action.gmtOffset)
        : endDate;

      const exceptions = getExceptionDates(recurrence, action);

      const customs = recurrence.exceptions?.custom;

      const instances: ActionEventType[] = [];
      let current = start;
      const validDays = recurrence.rules[0]?.by_day;
      const interval = recurrence.rules[0]?.interval || 1;
      const isWeeklyRecurrence = frequency === 'weekly';
      const maxOccurrences = recurrence?.rules[0]?.count;
      let occurrenceCount = 0;
      let safety = 0;

      // Only occurrences inside the selected week survive the per-day grouping below, so
      // long-running series skip straight to it. Capped rules (count) must enumerate from
      // the start, and a truthy-but-EMPTY by_day must not clamp: this loop's `validDays ?`
      // truthiness advance walks (and emits) daily for that shape, unlike the clamp's
      // plain-weekly jump.
      if (startDate && !maxOccurrences && !(validDays && validDays.length === 0)) {
        current = fastForwardRecurrenceCursor(current, startDate, frequency, interval, validDays);
      }

      while (
        (!until || isBefore(current, until) || isEqual(current, until)) &&
        (!maxOccurrences || occurrenceCount < maxOccurrences) &&
        safety++ < 10000 // safety cap: never loop forever on malformed recurrence data
      ) {
        const candidate = current;
        let formattedCandidate = formatDateToString(candidate);

        // Apply timezone conversion if the action has scheduledTime
        if (action.scheduledTime) {
          const { scheduledDate } = localDateToUTC(candidate);
          formattedCandidate = scheduledDate;
        }

        const progressStatus =
          customs?.[formattedCandidate]?.progressStatus === COMPLETE ||
          customs?.[formattedCandidate]?.progressStatus === INCOMPLETE ||
          customs?.[formattedCandidate]?.progressStatus === IN_PROGRESS
            ? customs?.[formattedCandidate]?.progressStatus
            : action?.progressStatus;

        const dateOfStarring = customs?.[formattedCandidate]?.dateOfStarring
          ? customs?.[formattedCandidate]
          : action?.dateOfStarring;

        const isException = isExceptionDate(candidate, exceptions);

        const isValidWeekly = validDays?.length && !isValidWeeklyDay(candidate, validDays);

        const isValidRecurrence =
          frequency === 'daily' ||
          (!isValidWeekly && isWeeklyRecurrence) ||
          frequency === 'monthly' ||
          frequency === 'yearly';

        if (!isException && isValidRecurrence) {
          instances.push({
            id: String(action.event?.id) + '__' + formattedCandidate,
            action: {
              ...action,
              typeName: 'Action',
              scheduledDate: formattedCandidate,
              progressStatus: progressStatus as ProgressStatus,
              dateOfStarring: dateOfStarring as Date,
            },
            actionId: action.id + formattedCandidate,
            typeName: 'Event',
          });
          occurrenceCount++;
        }

        if (validDays?.length && shouldIncrementByWeek(current, validDays) && isWeeklyRecurrence && interval > 1) {
          const daysFromMonday = (current.getDay() + 6) % 7;
          current = add(current, { days: -daysFromMonday + interval * 7 });
        } else {
          if (frequency === 'daily') {
            current = addDays(current, interval);
          } else if (isWeeklyRecurrence) {
            current = validDays ? addDays(current, 1) : addWeeks(current, interval);
          } else if (frequency === 'monthly') {
            current = addMonths(current, interval);
          } else if (frequency === 'yearly') {
            current = addYears(current, interval);
          }
        }
      }

      return instances;
    });

    if (actionEvents.length) {
      events = [...events, ...actionEvents];
    }
  });

  let categories = category ?? [];

  let externalCategory: Category;

  if (externalEvents?.length) {
    const formattedExternalEvents = externalEvents.map((event) => createExternalActionAdapter(event, cronofyProfiles));

    externalCategory = {
      id: 'external',
      order: null,
      color: 'gray.300',
      icon: null,
      name: 'External',
    };

    events = [...events, ...formattedExternalEvents];
    categories = [...categories, externalCategory];
  }
  let categoryWithEvents: DataType[] = [];

  categories?.forEach((category) => {
    let eventsPerCategory: Record<string, EventsPerDayType> = {};

    const actions = getActionsByCategoryId(
      (action ?? [])?.map((a) => a),
      category.id,
    );

    if (category?.id !== 'external' && isEmpty(actions)) return {};

    weekDays.forEach((day, index) => {
      const filteredEvents: ActionEventType[] = [];

      const eventsForAction = events.filter((e) => e.action.categoryId === category.id);

      eventsForAction.forEach((e) => {
        const eventOnDay = eventFor(e, day);

        if (eventOnDay) {
          filteredEvents.push(eventOnDay);
        }
      });

      const key = `event-${category.id}-${index + 1}`;

      eventsPerCategory = {
        ...eventsPerCategory,
        [key]: {
          // for the future, we will have a request to order by action scheduledTime all the events with animation
          // this is the code without the animation
          // events: orderBy(filteredEvents, (event) => event.action.scheduledTime, 'asc'),
          events: filteredEvents,
          day,
        },
      };
    });

    categoryWithEvents.push({
      category: {
        order: category.order,
        color: category.color,
        icon: category.icon,
        id: category.id,
        name: fixUncategorizedName(category.name),
      },
      events: { ...eventsPerCategory },
    });
  });

  const header = weekDays.map((day) => {
    // TODO: this is a duplicate of makeHeaderDuration function in DragAndDropCalendar/context.tsx, fix
    const dateDay = new Date(day);

    const filteredEvents = events?.reduce<Action[]>((acc, event) => {
      if (!event.action.scheduledDate) {
        return acc;
      }

      if (event.action.progressStatus === 'complete') {
        return acc;
      }

      const eventLocalDateStr = getLocalDateStringInTimezone(
        event.action.scheduledDate,
        event.action.scheduledTime,
        event.action.timezone,
        event.action.gmtOffset,
      );
      const dayDateStr = format(parseISO(day), 'yyyy-MM-dd');

      if (eventLocalDateStr === dayDateStr) {
        return [...acc, event.action];
      }

      return acc;
    }, []);

    return {
      weekDay: format(dateDay, 'eee'),
      monthDay: format(dateDay, 'dd'),
      totalStarredActions: getTotalDurationOfStarredActions(filteredEvents) || '0h',
      totalDuration: humanDuration(calculateTotalDuration(filteredEvents) * 60) || '0h',
      dateDay,
    };
  });

  return {
    header,
    data: categoryWithEvents,
  };
}

export {
  checkIfEventExists,
  createActionEvent,
  eventFor,
  formatActions,
  getDailyActionBackgroundColor,
  getEventByActionId,
  getEventById,
  getWeeklyActionBackgroundColor,
  groupActionsByBlockId,
  groupEventsByWeekDay,
  isSameEventDay,
  makeBlockItemKey,
  makeEvents,
};
